import type { EditorLine } from "@/components/commercial/service-editor";

export type ProposalValues = {
  assignee_id: string;
  helper_ids: string[];
  bay_id: string;
  starts_at: string;
  ends_at: string;
  starts_at_offset: string;
  ends_at_offset: string;
  discount_amount: string;
  services: EditorLine[];
};
export type ProposalDraft = {
  request_revision: string;
  revision: string;
  proposal: ProposalValues;
};
export function proposalDraftKey(context: {
  account: string;
  actor: string;
  location: string;
  request: string;
}) {
  return JSON.stringify([
    context.account,
    context.actor,
    context.location,
    context.request,
  ]);
}
const prefix = "garageclerk:proposal:";
const fields = [
  "assignee_id",
  "bay_id",
  "starts_at",
  "ends_at",
  "starts_at_offset",
  "ends_at_offset",
  "discount_amount",
] as const;
const storedFields = [...fields, "helper_ids", "services"] as const;
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export function restoreProposalDraft(
  key: string,
  fallback: ProposalDraft,
): ProposalDraft {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = sessionStorage.getItem(prefix + key);
    if (!raw || raw.length > 256 * 1024) return fallback;
    const saved: unknown = JSON.parse(raw);
    if (
      !object(saved) ||
      typeof saved.expires !== "number" ||
      saved.expires <= Date.now() ||
      !object(saved.proposal) ||
      typeof saved.request_revision !== "string" ||
      typeof saved.revision !== "string"
    )
      return fallback;
    const proposal = saved.proposal;
    if (
      !fields.every((field) => typeof proposal[field] === "string") ||
      !Array.isArray(proposal.helper_ids) ||
      !proposal.helper_ids.every((id) => typeof id === "string") ||
      !Array.isArray(proposal.services) ||
      !proposal.services.every(
        (line) =>
          object(line) &&
          ["offering_id", "quantity", "unit_price"].every(
            (field) => typeof line[field] === "string",
          ),
      )
    )
      return fallback;
    return {
      request_revision: saved.request_revision,
      revision: saved.revision,
      proposal: Object.fromEntries(
        storedFields.map((field) => [field, proposal[field]]),
      ) as ProposalValues,
    };
  } catch {
    return fallback;
  }
}

export function rememberProposalDraft(
  key: string,
  draft: ProposalDraft,
): boolean {
  const { proposal } = draft;
  try {
    const keys = Object.keys(sessionStorage).filter((item) =>
      item.startsWith(prefix),
    );
    for (const item of keys.slice(0, Math.max(0, keys.length - 19)))
      if (item !== prefix + key) sessionStorage.removeItem(item);
    const values = Object.fromEntries(
      storedFields.map((field) => [field, proposal[field]]),
    );
    const raw = JSON.stringify({
      expires: Date.now() + 24 * 60 * 60 * 1000,
      request_revision: draft.request_revision,
      revision: draft.revision,
      proposal: values,
    });
    if (raw.length > 256 * 1024) return false;
    sessionStorage.setItem(prefix + key, raw);
    return true;
  } catch {
    return false;
  }
}

export function forgetProposalDraft(key: string) {
  try {
    sessionStorage.removeItem(prefix + key);
  } catch {
    /* Storage can be denied. */
  }
}
